<template>
  <div class="linking-barcode-info">
    <el-collapse-item>
      <template #title>
        <BarcodeInfoLabel
          class="barcode-info-label"
          v-if="barcode"
          :label="barcodeTypeName"
          :subLabel="barcode.id"
          :barcodeTypeInfo="barcodeTypeInfo"
          @subLabel:click="moveTo(barcode.id)"
        >
          <template v-slot:action-menu v-if="displayMenu">
            <div class="action-menu-btn">
              <el-dropdown trigger="click">
                <span class="el-dropdown-link">
                  <el-button circle size="mini" type="text" :class="'text-gray-400'">
                    <IconVerticalThreeDots class="icon" />
                  </el-button>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item class="dangerous-action-lbl" @click="dialogVisible = true">
                      {{ $t('delete_barcode_link') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </template>
        </BarcodeInfoLabel>
      </template>
      <div class="flex" v-if="barcode.activationData?.trackingNumber">
        <div class="w-1/2 flex justify-end mr-1">
          <span>{{ trackingNumberLabel }}: </span>
        </div>
        <div :class="[barcode?.activationData?.trackingNumber.trim() === '***' ? 'text-red-500' : '']">
          <span>{{ barcode.activationData?.trackingNumber }}</span>
        </div>
      </div>
      <div class="flex" v-if="barcode.activationData?.externalId">
        <div class="w-1/2 flex justify-end mr-1">
          <span>{{ externalIdLabel }}: </span>
        </div>
        <div :class="[barcode?.activationData?.externalId.trim() === '***' ? 'text-red-500' : '']">
          <span>{{ barcode.activationData?.externalId }}</span>
        </div>
      </div>
      <div class="flex" v-for="(value, key) in dimension" :key="key">
        <div v-if="value.value" class="flex justify-end mr-1" style="width: 50%">
          <span v-if="$te(toSnakeCase(value.label))"> {{ $t(toSnakeCase(value.label)) }}: </span>
          <span v-else>{{ value.label }}: </span>
        </div>
        <div class="flex-grow break-all" style="width: 50%">
          <span :class="[getDisplayValue(value.value) === '***' ? 'text-red-500' : '']">{{
            `${getDisplayValue(value.value)}`
          }}</span>
        </div>
      </div>
    </el-collapse-item>
    <div class="dialog-area">
      <el-dialog center v-model="dialogVisible" width="90%" top="0" :show-close="false" :destroy-on-close="false">
        <div class="dialogMessage">
          {{ $t('delete_barcode_linking_confirm') }}
        </div>
        <template #footer>
          <span class="dialog-footer">
            <el-button round class="cancel-btn" @click="dialogVisible = false">
              {{ $t('cancel') }}
            </el-button>
            <el-button circle type="primary" @click="onUnlink">
              {{ $t('ok') }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'
import { MASKED_STR, LINKED_BARCODE_HREF_TYPE, LINKED_BARCODE_PATH_KEY } from '../utils/constants'
import isEmpty from 'lodash/isEmpty'
import { parseCustomFieldsValue } from '@/utils/barcodeHelper'
import { deepSearch, objectToArray, toSnakeCase } from '@/utils/helpers'
import { IBarcode, IActivationData, IBarcodeDefinitionType, IFormInputSchema, IDimension, ILabelDisplay, IProduct, IRedirectData } from '@/utils/types'
import IconVerticalThreeDots from './svg/IconVerticalThreeDots.vue'
import BarcodeInfoLabel from './BarcodeInfoLabel.vue'

@Options({
  components: { BarcodeInfoLabel, IconVerticalThreeDots },
  emits: ['barcode:unlink', 'barcode:redirect'],
})
export default class LinkingBarcodeInfo extends Vue {
  @Prop({ type: String }) readonly mainBarcodeId!: string
  @Prop({ type: String }) readonly trackingNumberMes!: string
  @Prop({ type: Object }) readonly barcode!: IBarcode
  @Prop({ type: Object }) readonly barcodeTypeInfo!: IBarcodeDefinitionType
  @Prop({ type: Object }) readonly product?: IProduct
  @Prop({ type: String }) readonly mainKey!: string
  @Prop({ type: Number }) readonly indexMainKey?: number
  @Prop({ type: Number }) readonly indexLinkedBarcode!: number
  @Prop({ default: false }) readonly displayMenu?: boolean
  @Prop({ type: String }) readonly linkedBarcodeHrefType!: string

  dialogVisible = false
  customPackageInfo = {} as Record<string, ILabelDisplay>

  get barcodeTypeName() {
    return this.barcodeTypeInfo?.name
  }

  get trackingNumberLabel() {
    const tpLabel = this.barcodeTypeInfo?.activationFields?.trackingNumber?.label
    return !isEmpty(tpLabel) ? tpLabel : this.$t('tracking_number')
  }

  get dimension() {
    this.setCustomFieldsValue()
    const rawDimensionData = {
      ...this.barcode?.activationData?.dimension,
    } as IDimension
    const localeNumber = {
      width: rawDimensionData?.width?.toLocaleString(),
      height: rawDimensionData?.height?.toLocaleString(),
      weight: rawDimensionData?.weight?.toLocaleString(),
      depth: rawDimensionData?.depth?.toLocaleString(),
    }

    const dimension = Object.entries(localeNumber).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          label: key,
          value: value,
        },
      }),
      {}
    )

    const packageInfo = {
      // ...Product
      ...(this.product && { productName: this.product.name }),

      // ...Package info (dimension)
      ...(this.isDimensionAvailable && this.barcode?.activationData?.dimension && dimension),
      // ...Custom fields
      ...this.customPackageInfo,
    }

    return { ...packageInfo }
  }

  get externalIdLabel(): string {
    return this.$t('externalId')
  }

  get maskedCustomField() {
    if (!this.barcodeTypeInfo) return
    const ignoredKey = ['imagePaths', 'imageUrls', 'filePaths', 'productId', 'externalId']

    const maskedCustomField = {} as Record<string, string | string[]>
    const maskedFields =
      this.barcode?.activationData?.maskedFields?.filter((val: string) => {
        if (val !== 'origin' && val !== 'destination') {
          return val
        }
      }) || []
    maskedFields.forEach((key: string) => {
      let result: IFormInputSchema = {}
      deepSearch(
        this.barcodeTypeInfo?.activationFields,
        key.split('.').length > 0 ? key.split('.')[1] : key,
        (k: string, v: IFormInputSchema) => (result = v)
      )

      if (result.isHidden) {
        return
      }

      if (result?.label) {
        maskedCustomField[result?.label] = MASKED_STR
      } else {
        const arr = objectToArray<Record<string, string>>(result as Record<string, Record<string, string>>) || []
        arr.forEach((el) => {
          if (el?.value?.label) {
            maskedCustomField[el?.value?.label] = MASKED_STR
          }
        })
      }

      if (key === 'dimension') {
        delete maskedCustomField[key]
        maskedCustomField.width = MASKED_STR
        maskedCustomField.height = MASKED_STR
        maskedCustomField.depth = MASKED_STR
        maskedCustomField.weight = MASKED_STR
      }
      if (ignoredKey.includes(key)) {
        delete maskedCustomField[key]
      }
    })
    return maskedCustomField
  }

  @Emit('barcode:unlink')
  onUnlink() {
    this.dialogVisible = false
    return this.mainBarcodeId
  }

  @Emit('barcode:redirect')
  moveTo(): IRedirectData {
    return this.linkedBarcodeHrefType && this.linkedBarcodeHrefType !== LINKED_BARCODE_HREF_TYPE.snapshot
      ? { mainBarcodeId: this.barcode.id }
      : {
          mainBarcodeId: this.mainBarcodeId,
          path:
            this.mainKey === LINKED_BARCODE_PATH_KEY.activationData
              ? `${this.mainKey}.${LINKED_BARCODE_PATH_KEY.linkedBarcodes}.${this.indexLinkedBarcode}`
              : `${this.mainKey}.${this.indexMainKey}.${LINKED_BARCODE_PATH_KEY.linkedBarcodes}.${this.indexLinkedBarcode}`,
        }
  }

  toSnakeCase(s: string) {
    return toSnakeCase(s)
  }

  getDisplayValue(value: string | number) {
    return typeof value === 'number' ? value.toFixed(1) : value
  }

  flattenFields(obj: IActivationData): Record<string, string | string[]> {
    const fields: { [key: string]: unknown } = { ...obj }
    return Object.keys(fields).reduce(
      (acc, cur) => (cur.match(/^custom[a-zA-Z]/g) ? { ...acc, ...(fields[cur] as object) } : { ...acc }),
      {}
    )
  }

  get isDimensionAvailable() {
    return this.barcodeTypeInfo?.activationFields?.dimension?.isAvailable
  }

  setCustomFieldsValue() {
    if (!this.barcodeTypeInfo) return
    const customFieldsDefinition = this.barcodeTypeInfo?.activationFields?.customFields || {}
    parseCustomFieldsValue(this.customPackageInfo, customFieldsDefinition, this.barcode)
  }
}
</script>

<style lang="scss" scoped>
.linking-barcode-info :deep() {
  background-color: #e0e0e0;
  border-radius: 10px;
  margin-top: -11px;
  padding: 18px 0px;
  border: 1px solid #fff;

  .el-collapse-item {
    margin-bottom: 8px;
  }

  .el-collapse-item {
    margin-bottom: 0px;
  }

  .el-collapse-item__header,
  .el-collapse-item__wrap {
    border-bottom: none;
    background-color: inherit;
  }

  .el-collapse-item__content {
    padding: 8px 22px 4px;
  }

  .el-collapse-item__arrow {
    display: none;
  }
}

.dialog-area :deep() {
  .cancel-btn {
    &.is-round {
      height: 64px;
      border: 1px solid #828282;
      border-radius: 40px !important;
      box-sizing: border-box !important;
    }
  }

  &.is-circle {
    min-width: 64px;
    border-radius: 32px !important;
  }
}
</style>
